import classNames from 'classnames/bind';
import { cloneElement, ReactElement } from 'react';

import { TabProps } from './Tab';
import classes from './Tabs.module.scss';

const cx = classNames.bind(classes);

export interface TabsProps {
    /** tabId of the tab you want selected. Otherwise the first one will be selected. */
    activeTab?: string;
    /** Aria label for the tab list */
    'aria-label'?: string;
    /** Tab components */
    children: ReactElement<TabProps> | ReactElement<TabProps>[];
    /** Additional CSS classes */
    className?: string;
    /** Function to call when tab clicked */
    onTabClicked: (newTab: string) => void;
}

function WrappedTabs({
    activeTab,
    'aria-label': ariaLabel = 'Tabs',
    children,
    className,
    onTabClicked,
}: TabsProps) {
    const tabs = Array.isArray(children) ? children : [children];

    return (
        <>
            <div aria-label={ariaLabel} className={cx('tab-nav', className)} role="tablist">
                {tabs.map((tab) => {
                    const { className, label, onMouseOver, tabId } = tab.props;
                    const isActive = tabId === activeTab;
                    const classes =
                        typeof className === 'function' ? className({ isActive }) : className;

                    return (
                        <button
                            aria-controls={`tab-panel-${tabId}`}
                            aria-selected={isActive}
                            className={cx(
                                'tab',
                                {
                                    'tab--active': isActive,
                                },
                                classes
                            )}
                            id={`tab-nav-${tabId}`}
                            key={tabId}
                            onClick={(e) => {
                                e.stopPropagation();
                                onTabClicked(tabId);
                            }}
                            onFocus={onMouseOver}
                            onMouseOver={onMouseOver}
                            role="tab"
                            tabIndex={isActive ? 0 : -1}
                        >
                            {label}
                        </button>
                    );
                })}
            </div>
            {tabs.map((tab) => {
                const { tabId } = tab.props;

                if (tabId !== activeTab) return null;

                return cloneElement(tab, {
                    'aria-labelledby': `tab-nav-${tabId}`,
                    id: `tab-panel-${tabId}`,
                    key: tabId,
                });
            })}
        </>
    );
}

export default function Tabs({ activeTab, children, ...rest }: TabsProps) {
    const firstChild = Array.isArray(children) ? children[0] : children;
    const currentActiveTab = activeTab ?? firstChild.props.tabId;

    return (
        <WrappedTabs {...rest} activeTab={currentActiveTab}>
            {children}
        </WrappedTabs>
    );
}
